import {
  useEffect,
  useState
} from "react";

import {
  useNavigate
} from "react-router-dom";

import api from "../api";

import {
  formatMoney
} from "../utils/formatMoney";

import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid
} from "recharts";

import {
  Filter
} from "lucide-react";

function DepensesList() {

  const [data, setData] =
    useState([]);

  const [categorie, setCategorie] =
    useState("");

  const [dateDebut, setDateDebut] =
    useState("");

  const [dateFin, setDateFin] =
    useState("");

  const [editing, setEditing] =
    useState(null);

  const [deleting, setDeleting] =
    useState(null);

  const [confirmText, setConfirmText] =
    useState("");

  const [page, setPage] =
    useState(1);

  const [totalPages, setTotalPages] =
    useState(1);

  const limit = 20;

  const navigate =
    useNavigate();

  const role =
    (
      localStorage.getItem(
        "role"
      ) || ""
    ).toLowerCase();

  const isComptable =
    role === "comptable" ||
    role === "admin";

  // =====================
  // FETCH
  // =====================

  const fetchDepenses =
    async () => {

      try {

        const res =
          await api.get(
            "/depenses",
            {
              params: {
                page,
                limit,
                categorie,
                dateDebut,
                dateFin
              }
            }
          );

        if (
          Array.isArray(
            res.data
          )
        ) {

          setData(
            res.data
          );

          setTotalPages(1);

        } else {

          setData(
            res.data.data ||
              []
          );

          setTotalPages(
            res.data
              .totalPages ||
              1
          );

        }

      } catch (err) {

        console.error(err);

      }
    };

  useEffect(() => {
    fetchDepenses();
  }, [
    page,
    categorie,
    dateDebut,
    dateFin
  ]);

  useEffect(() => {
    setPage(1);
  }, [
    categorie,
    dateDebut,
    dateFin
  ]);

  // =====================
  // DESCRIPTION
  // =====================

  const getDescription =
    (d) => {

      if (
        d.categorie ===
          "salaires" &&
        d.periode_salaire
      ) {

        const [
          y,
          m
        ] =
          d.periode_salaire.split(
            "-"
          );

        const date =
          new Date(
            y,
            m - 1
          );

        const mois =
          date.toLocaleString(
            "fr-FR",
            {
              month:
                "long",

              year:
                "numeric"
            }
          );

        return `Salaire ${mois}`;
      }

      return (
        d.description ||
        "Description manquante"
      );
    };

  // =====================
  // DELETE
  // =====================

  const confirmDelete =
    async () => {

      if (
        confirmText !==
        "SUPPRIMER"
      ) {

        return alert(
          "Veuillez taper SUPPRIMER"
        );
      }

      if (
        !window.confirm(
          "⚠️ Confirmer la suppression ?"
        )
      ) {
        return;
      }

      try {

        await api.delete(
          `/depenses/${deleting.id}`
        );

        alert(
          "✅ Dépense supprimée"
        );

        setDeleting(null);

        setConfirmText("");

        fetchDepenses();

      } catch (err) {

        console.error(err);

        alert(
          "❌ Erreur"
        );

      }
    };

  // =====================
  // UPDATE
  // =====================

  const handleUpdate =
    async () => {

      if (
        !window.confirm(
          "Confirmer modification ?"
        )
      ) {
        return;
      }

      try {

        await api.put(
          `/depenses/${editing.id}`,
          editing
        );

        alert(
          "✅ Dépense modifiée"
        );

        setEditing(null);

        fetchDepenses();

      } catch (err) {

        console.error(err);

        alert(
          "❌ Erreur"
        );

      }
    };

  // ==============================
  // OUVERTURE MODIFICATION
  // ==============================

  const openEdit = (depense) => {
    setEditing({
      ...depense
    });
  };

  // ==============================
  // OUVERTURE SUPPRESSION
  // ==============================

  const openDelete = (depense) => {
    setDeleting(depense);
    setConfirmText("");
  };

  // =====================
  // STATS
  // =====================

  const total =
    data.reduce(
      (sum, d) =>
        sum +
        Number(
          d.montant || 0
        ),
      0
    );

  const statsCategorie =
    Object.values(
      data.reduce(
        (acc, d) => {

          if (
            !acc[
              d.categorie
            ]
          ) {

            acc[
              d.categorie
            ] = {
              name:
                d.categorie,

              value: 0
            };
          }

          acc[
            d.categorie
          ].value +=
            Number(
              d.montant
            );

          return acc;

        },
        {}
      )
    );

  const statsTemps =
    Object.values(
      data.reduce(
        (acc, d) => {

          const date =
            new Date(
              d.date_depense
            ).toLocaleDateString();

          if (
            !acc[date]
          ) {

            acc[date] = {
              date,
              montant: 0
            };
          }

          acc[date].montant +=
            Number(
              d.montant
            );

          return acc;

        },
        {}
      )
    );

  const COLORS = [
    "#2563eb",
    "#16a34a",
    "#dc2626",
    "#f59e0b",
    "#7c3aed"
  ];

  // =====================
  // RENDER
  // =====================

  return (
    <div className="p-3 md:p-6 space-y-4 md:space-y-6 min-h-[100dvh] bg-gray-100">

      {/* HEADER */}
      <div className="flex flex-col md:flex-row justify-between md:items-center gap-3">

        <h2 className="text-2xl md:text-3xl font-semibold">
          Dépenses
        </h2>

        <div className="flex flex-col md:flex-row gap-2 w-full md:w-auto">

          <button
            onClick={() =>
              navigate(
                "/depenses/ajouter"
              )
            }
            className="bg-blue-600 hover:bg-blue-700 transition text-white px-4 py-3 rounded-xl"
          >
            ➕ Ajouter
          </button>

          <button
            onClick={() =>
              navigate(
                "/audit-logs"
              )
            }
            className="bg-purple-600 hover:bg-purple-700 transition text-white px-4 py-3 rounded-xl"
          >
            📊 Historique
          </button>

        </div>

      </div>

      {/* FILTERS */}
      <div className="bg-white p-4 rounded-2xl shadow flex flex-col md:flex-row md:flex-wrap gap-3">

        <div className="flex items-center gap-2 text-gray-600">
          <Filter size={16} />
          <span className="text-sm">
            Filtres
          </span>
        </div>

        <select
          value={categorie}
          onChange={(e) =>
            setCategorie(
              e.target.value
            )
          }
          className="border px-4 py-3 rounded-xl outline-none focus:ring-2 focus:ring-blue-400"
        >

          <option value="">
            Toutes catégories
          </option>

          {[
            ...new Set(
              data.map(
                (d) =>
                  d.categorie
              )
            )
          ].map((c, i) => (

            <option
              key={i}
            >
              {c}
            </option>

          ))}

        </select>

        <input
          type="date"
          value={dateDebut}
          onChange={(e) =>
            setDateDebut(
              e.target.value
            )
          }
          className="border px-4 py-3 rounded-xl outline-none focus:ring-2 focus:ring-blue-400"
        />

        <input
          type="date"
          value={dateFin}
          onChange={(e) =>
            setDateFin(
              e.target.value
            )
          }
          className="border px-4 py-3 rounded-xl outline-none focus:ring-2 focus:ring-blue-400"
        />

      </div>

      {/* CHARTS */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

        <div className="bg-white rounded-2xl shadow p-3">

          <ResponsiveContainer
            width="100%"
            height={250}
          >

            <PieChart>

              <Pie
                data={
                  statsCategorie
                }
                dataKey="value"
              >

                {statsCategorie.map(
                  (_, i) => (

                    <Cell
                      key={i}
                      fill={
                        COLORS[
                          i % 5
                        ]
                      }
                    />

                  )
                )}

              </Pie>

              <Tooltip
                formatter={(v) =>
                  formatMoney(
                    v
                  )
                }
              />

            </PieChart>

          </ResponsiveContainer>

        </div>

        <div className="bg-white rounded-2xl shadow p-3">

          <ResponsiveContainer
            width="100%"
            height={250}
          >

            <BarChart
              data={statsTemps}
            >

              <CartesianGrid strokeDasharray="3 3" />

              <XAxis
                dataKey="date"
                hide
              />

              <YAxis />

              <Tooltip
                formatter={(v) =>
                  formatMoney(
                    v
                  )
                }
              />

              <Bar dataKey="montant" />

            </BarChart>

          </ResponsiveContainer>

        </div>

      </div>

      {/* MOBILE */}
      <div className="grid gap-3 md:hidden">

        {data.map((d) => (

          <div
            key={d.id}
            className="bg-white rounded-2xl shadow border p-4"
          >

            <div className="flex justify-between items-start gap-2">

              <div className="font-semibold break-words">
                {getDescription(
                  d
                )}
              </div>

              <div className="text-blue-600 font-bold whitespace-nowrap">
                {formatMoney(
                  d.montant
                )}
              </div>

            </div>

            <div className="mt-2 text-sm text-gray-500">
              {
                d.categorie
              }
            </div>

            <div className="text-xs text-gray-400 mt-1">
              {new Date(
                d.date_depense
              ).toLocaleDateString()}
            </div>

            {isComptable && (
              <div className="grid grid-cols-2 gap-2 mt-4 pt-3 border-t">

                <button
                  type="button"
                  onClick={() => openEdit(d)}
                  className="flex items-center justify-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-white py-2.5 rounded-xl text-sm font-medium transition"
                >
                  <span>✏️</span>
                  <span>Modifier</span>
                </button>

                <button
                  type="button"
                  onClick={() => openDelete(d)}
                  className="flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white py-2.5 rounded-xl text-sm font-medium transition"
                >
                  <span>🗑️</span>
                  <span>Supprimer</span>
                </button>

              </div>
            )}

          </div>

        ))}

        <div className="bg-gray-900 text-white rounded-2xl p-4 flex justify-between items-center">

          <span className="font-semibold">
            TOTAL
          </span>

          <span className="font-bold">
            {formatMoney(
              total
            )}
          </span>

        </div>

      </div>

      {/* DESKTOP */}
      <div className="hidden md:block overflow-x-auto bg-white rounded-2xl shadow">

        <table className="min-w-[700px] w-full text-sm">

          <thead className="bg-gray-900 text-white">

            <tr>

              <th className="px-3 py-3 text-left">
                Date
              </th>

              <th className="px-3 py-3 text-left">
                Description
              </th>

              <th className="px-3 py-3 text-left">
                Catégorie
              </th>

              <th className="px-3 py-3 text-right">
                Montant
              </th>

              {isComptable && (

                <th className="px-3 py-3 text-center">
                  Action
                </th>

              )}

            </tr>

          </thead>

          <tbody>

            {data.map((d) => (

              <tr
                key={d.id}
                className="border-t hover:bg-gray-50 transition"
              >

                <td className="px-3 py-3 whitespace-nowrap">
                  {new Date(
                    d.date_depense
                  ).toLocaleDateString()}
                </td>

                <td className="px-3 py-3">
                  {getDescription(
                    d
                  )}
                </td>

                <td className="px-3 py-3">
                  {
                    d.categorie
                  }
                </td>

                <td className="px-3 py-3 text-right text-blue-600 font-semibold whitespace-nowrap">
                  {formatMoney(
                    d.montant
                  )}
                </td>

                {isComptable && (
                  <td className="px-3 py-3 text-center align-middle">

                    <div className="flex items-center justify-center gap-2">

                      <button
                        type="button"
                        onClick={() => openEdit(d)}
                        className="inline-flex items-center justify-center gap-1.5 bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-2 rounded-lg text-xs font-medium transition"
                        title="Modifier cette dépense"
                      >
                        <span>✏️</span>
                        <span>Modifier</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => openDelete(d)}
                        className="inline-flex items-center justify-center gap-1.5 bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-lg text-xs font-medium transition"
                        title="Supprimer cette dépense"
                      >
                        <span>🗑️</span>
                        <span>Supprimer</span>
                      </button>

                    </div>

                  </td>
                )}
              </tr>

            ))}

            <tr className="bg-gray-100 font-bold">

              <td
                colSpan={
                  isComptable
                    ? 3
                    : 3
                }
                className="px-3 py-3"
              >
                TOTAL
              </td>

              <td className="px-3 py-3 text-right">
                {formatMoney(
                  total
                )}
              </td>

              {isComptable && (
                <td />
              )}

            </tr>

          </tbody>

        </table>

      </div>

      {/* PAGINATION */}
      <div className="flex justify-center gap-2 flex-wrap">

        {Array.from(
          {
            length:
              totalPages
          },
          (_, i) => (

            <button
              key={i}
              onClick={() =>
                setPage(i + 1)
              }
              className={`px-4 py-2 rounded-xl text-sm transition ${
                page ===
                i + 1
                  ? "bg-blue-600 text-white"
                  : "bg-white hover:bg-gray-100"
              }`}
            >
              {i + 1}
            </button>

          )
        )}

      </div>

      {/* ============================== */}
      {/* MODALE MODIFICATION */}
      {/* ============================== */}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">

            <div className="p-5 border-b">
              <h3 className="text-xl font-bold">
                Modifier la dépense
              </h3>
            </div>

            <div className="p-5 space-y-4">

              {/* MONTANT */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Montant
                </label>

                <input
                  type="number"
                  value={editing.montant ?? ""}
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      montant: e.target.value
                    })
                  }
                  className="w-full border rounded-xl px-4 py-3"
                />
              </div>

              {/* CATEGORIE */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Catégorie
                </label>

                <select
                  value={editing.categorie ?? ""}
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      categorie: e.target.value
                    })
                  }
                  className="w-full border rounded-xl px-4 py-3"
                >
                  <option value="">Sélectionner</option>
                  <option value="salaires">Salaires</option>
                  <option value="fournitures">Fournitures</option>
                  <option value="transport">Transport</option>
                  <option value="maintenance">Maintenance</option>
                  <option value="loyer">Loyer</option>
                  <option value="autres">Autres</option>
                </select>
              </div>

              {/* DESCRIPTION */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Description
                </label>

                <textarea
                  value={editing.description ?? ""}
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      description: e.target.value
                    })
                  }
                  rows={3}
                  className="w-full border rounded-xl px-4 py-3"
                />
              </div>

              {/* MODE DE PAIEMENT */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Mode de paiement
                </label>

                <select
                  value={editing.mode_paiement ?? "cash"}
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      mode_paiement: e.target.value
                    })
                  }
                  className="w-full border rounded-xl px-4 py-3"
                >
                  <option value="cash">Cash</option>
                  <option value="mobile_money">Mobile Money</option>
                  <option value="banque">Banque</option>
                  <option value="carte">Carte</option>
                </select>
              </div>

              {/* PERIODE SALAIRE */}
              {editing.categorie === "salaires" && (
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Période de salaire
                  </label>

                  <input
                    type="month"
                    value={editing.periode_salaire ?? ""}
                    onChange={(e) =>
                      setEditing({
                        ...editing,
                        periode_salaire: e.target.value
                      })
                    }
                    className="w-full border rounded-xl px-4 py-3"
                  />
                </div>
              )}

            </div>

            {/* ACTIONS */}
            <div className="p-5 border-t flex gap-3 justify-end">

              <button
                type="button"
                onClick={() => setEditing(null)}
                className="px-4 py-3 rounded-xl bg-gray-200 hover:bg-gray-300"
              >
                Annuler
              </button>

              <button
                type="button"
                onClick={handleUpdate}
                className="px-4 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white"
              >
                Enregistrer
              </button>

            </div>

          </div>
        </div>
      )}

      {/* ============================== */}
      {/* MODALE SUPPRESSION */}
      {/* ============================== */}

      {deleting && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">

          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">

            <div className="p-5 border-b">
              <h3 className="text-xl font-bold text-red-600">
                Supprimer la dépense
              </h3>
            </div>

            <div className="p-5 space-y-4">

              <p className="text-gray-700">
                Vous êtes sur le point de supprimer :
              </p>

              <div className="bg-gray-50 rounded-xl p-4">
                <p className="font-semibold">
                  {getDescription(deleting)}
                </p>

                <p className="text-blue-600 font-bold mt-1">
                  {formatMoney(deleting.montant)}
                </p>

                <p className="text-sm text-gray-500 mt-1">
                  {deleting.categorie}
                </p>
              </div>

              <p className="text-sm text-red-600 font-medium">
                Cette opération est irréversible.
              </p>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Tapez SUPPRIMER pour confirmer
                </label>

                <input
                  type="text"
                  value={confirmText}
                  onChange={(e) =>
                    setConfirmText(e.target.value)
                  }
                  placeholder="SUPPRIMER"
                  className="w-full border rounded-xl px-4 py-3"
                />
              </div>

            </div>

            <div className="p-5 border-t flex gap-3 justify-end">

              <button
                type="button"
                onClick={() => {
                  setDeleting(null);
                  setConfirmText("");
                }}
                className="px-4 py-3 rounded-xl bg-gray-200 hover:bg-gray-300"
              >
                Annuler
              </button>

              <button
                type="button"
                disabled={confirmText !== "SUPPRIMER"}
                onClick={confirmDelete}
                className="px-4 py-3 rounded-xl bg-red-600 hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white"
              >
                Supprimer définitivement
              </button>

            </div>

          </div>
        </div>
      )}

    </div>
  );
}

export default DepensesList;